import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Sparkles, Check, Crown, Upload, Trash2, ImageIcon, UserPlus, Pencil, X, Lock, Gift, Copy, XCircle, ShieldCheck } from "lucide-react";
import api, { formatApiError, setToken } from "@/lib/api";
import { formatDateShort } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import MySubscriptionInvoicesCard from "@/components/settings/MySubscriptionInvoicesCard";

const CURRENCIES = ["EUR", "USD", "GBP", "MXN", "ARS", "COP", "CLP", "BRL", "CHF", "JPY"];
const PLAN_FEATURES_KEY = "allFeaturesIncluded";

export default function Settings() {
  const { user, setUser } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [name, setName] = useState(user?.name || "");
  const [baseCurrency, setBaseCurrency] = useState(user?.base_currency || "EUR");
  const [language, setLanguage] = useState(user?.language || lang);
  const [priceAlertThreshold, setPriceAlertThreshold] = useState(
    user?.price_alert_threshold != null ? String(user.price_alert_threshold) : "5"
  );
  // Fiscal profile (razón social, CIF, dirección). Alimenta el filtro OCR
  // que descarta el propio CIF cuando aparece en las facturas del proveedor.
  // Sólo editable por el owner del tenant.
  const [companyName, setCompanyName] = useState(user?.company_name || "");
  const [companyTaxId, setCompanyTaxId] = useState(user?.company_tax_id || "");
  const [companyAddress, setCompanyAddress] = useState(user?.company_address || "");
  // iter 64 — extra fiscal fields
  const [companyCity, setCompanyCity] = useState(user?.company_city || "");
  const [companyZip, setCompanyZip] = useState(user?.company_zip || "");
  const [companyCountry, setCompanyCountry] = useState(user?.company_country || "");
  const [companyPhone, setCompanyPhone] = useState(user?.company_phone || "");
  const [companyEmail, setCompanyEmail] = useState(user?.company_email || "");
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState("monthly");

  const isOwner = (user?.team_role || "owner") === "owner";

  useEffect(() => {
    Promise.all([
      api.get("/billing/usage"),
      api.get("/billing/plans"),
    ])
      .then(([u, p]) => {
        setUsage(u.data);
        setPlans(p.data.plans || []);
        setBillingCycle(u.data.billing_cycle || "monthly");
      })
      .catch((err) => console.error("billing fetch failed:", err));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name,
        base_currency: baseCurrency,
        language,
        price_alert_threshold: priceAlertThreshold === "" ? 0 : parseFloat(priceAlertThreshold),
      };
      // Fiscal fields are only meaningful for the owner; the server enforces
      // that too (403 otherwise), but we skip sending them for team members
      // to keep the payload clean.
      if (isOwner) {
        payload.company_name = companyName;
        payload.company_tax_id = companyTaxId;
        payload.company_address = companyAddress;
        payload.company_city = companyCity;
        payload.company_zip = companyZip;
        payload.company_country = companyCountry;
        payload.company_phone = companyPhone;
        payload.company_email = companyEmail;
      }
      const { data } = await api.put("/users/me", payload);
      setUser(data);
      setLang(language);
      toast.success(t("accountSaved"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    if (planId === usage?.plan) return;
    try {
      await api.post("/billing/upgrade", { plan: planId, billing_cycle: billingCycle });
      const { data } = await api.get("/billing/usage");
      setUsage(data);
      toast.success(t("upgrade"));
    } catch (err) {
      if (err?.response?.status === 501) {
        toast.info(t("paymentSoon"));
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
          {t("settings")}
        </p>
        <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
          {t("profile")}
        </h1>
      </div>

      {isOwner && <SubscriptionStatus usage={usage} />}

      {isOwner && <MySubscriptionInvoicesCard lang={lang} />}

      {isOwner && (
        <PricingTable
          plans={plans}
          currentPlan={usage?.plan || "free"}
          billingCycle={billingCycle}
          onCycleChange={setBillingCycle}
          onSelect={handleSelectPlan}
        />
      )}

      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 space-y-5 max-w-2xl">
        <Field label={t("email")}>
          <Input value={user?.email || ""} disabled />
        </Field>
        <Field label={t("name")}>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" />
        </Field>
        <Field label={t("baseCurrency")}>
          <Select value={baseCurrency} onValueChange={setBaseCurrency} disabled={!isOwner}>
            <SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("language")}>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger data-testid="settings-language"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("priceAlertThreshold")}>
          <Input
            type="number"
            step="0.5"
            min="0"
            value={priceAlertThreshold}
            onChange={(e) => setPriceAlertThreshold(e.target.value)}
            data-testid="settings-price-threshold"
            className="font-mono-num"
          />
          <p className="text-xs text-[var(--text-muted)] mt-1">{t("priceAlertHelp")}</p>
        </Field>
        <div className="pt-2">
          <Button onClick={save} disabled={saving} data-testid="settings-save-btn" className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            {saving ? "..." : t("save")}
          </Button>
        </div>
      </div>

      {isOwner && <CompanyLogoCard user={user} setUser={setUser} lang={lang} />}

      {isOwner && (
        <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 space-y-4 max-w-2xl" data-testid="fiscal-data-card">
          <div>
            <h2 className="font-display text-lg font-semibold">
              {lang === "es" ? "Datos fiscales" : "Fiscal profile"}
            </h2>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              {lang === "es"
                ? "El CIF se usa para descartar tu propio CIF cuando la IA lo detecta por error en las facturas de proveedores (aparece en el bloque \"Facturar a:\"). Aplica a subidas nuevas."
                : "Your tax ID is used to discard your own CIF when the OCR mistakes it for the vendor's tax id (i.e. picks it up from the \"Bill to:\" block). Applies to new uploads only."}
            </p>
          </div>
          <Field label={lang === "es" ? "Razón social" : "Legal name"}>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={lang === "es" ? "Mi Restaurante SL" : "My Restaurant SL"}
              data-testid="settings-company-name"
            />
          </Field>
          <Field label={lang === "es" ? "CIF / NIF / VAT" : "Tax ID"}>
            <Input
              value={companyTaxId}
              onChange={(e) => setCompanyTaxId(e.target.value.toUpperCase())}
              placeholder="B12345678"
              data-testid="settings-company-tax-id"
              className="font-mono"
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {lang === "es"
                ? "Se normaliza automáticamente (mayúsculas, sin espacios)."
                : "Auto-normalized (uppercase, no spaces)."}
            </p>
          </Field>
          <Field label={lang === "es" ? "Dirección fiscal" : "Fiscal address"}>
            <Input
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              placeholder={lang === "es" ? "Calle Mayor 1" : "Main St. 1"}
              data-testid="settings-company-address"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label={lang === "es" ? "Población" : "City"}>
              <Input
                value={companyCity}
                onChange={(e) => setCompanyCity(e.target.value)}
                placeholder={lang === "es" ? "Madrid" : "Madrid"}
                data-testid="settings-company-city"
              />
            </Field>
            <Field label={lang === "es" ? "Código postal" : "Zip code"}>
              <Input
                value={companyZip}
                onChange={(e) => setCompanyZip(e.target.value)}
                placeholder="28001"
                data-testid="settings-company-zip"
              />
            </Field>
            <Field label={lang === "es" ? "País" : "Country"}>
              <Input
                value={companyCountry}
                onChange={(e) => setCompanyCountry(e.target.value)}
                placeholder={lang === "es" ? "España" : "Spain"}
                data-testid="settings-company-country"
              />
            </Field>
          </div>
          <Field label={lang === "es" ? "Teléfono" : "Phone"}>
            <Input
              value={companyPhone}
              onChange={(e) => setCompanyPhone(e.target.value)}
              placeholder="+34 900 000 000"
              data-testid="settings-company-phone"
              type="tel"
            />
          </Field>
          <Field label={lang === "es" ? "Email de facturación" : "Billing email"}>
            <Input
              value={companyEmail}
              onChange={(e) => setCompanyEmail(e.target.value)}
              placeholder={lang === "es" ? "facturacion@empresa.com" : "billing@company.com"}
              data-testid="settings-company-email"
              type="email"
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {lang === "es"
                ? "Aparecerá como email fiscal en las facturas de suscripción emitidas por ChefSync. Si se deja vacío se usará tu email de acceso."
                : "Used on ChefSync subscription invoices. Falls back to your login email when empty."}
            </p>
          </Field>
          <div className="pt-1">
            <Button
              onClick={save}
              disabled={saving}
              data-testid="settings-fiscal-save-btn"
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            >
              {saving ? "..." : (lang === "es" ? "Guardar datos fiscales" : "Save fiscal profile")}
            </Button>
          </div>
        </div>
      )}

      {isOwner && <TeamManagementCard lang={lang} ownerEmail={user?.email} />}

      {/* Iter 63 — Referral program (Basic+ tenants). */}
      {isOwner && <ReferralsCard lang={lang} user={user} />}

      {/* Cambio de contraseña — cualquier miembro (owner / editor / viewer) puede
          cambiar la suya propia. Endpoint: POST /api/auth/change-password. */}
      <ChangePasswordCard lang={lang} />
    </div>
  );
}

// ============================================================ Company Logo
function CompanyLogoCard({ user, setUser, lang }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const logoUrl = user?.company_logo_url;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(lang === "es" ? "Solo imágenes" : "Image files only");
      return;
    }
    if (file.size > 1_048_576) {
      toast.error(lang === "es" ? "Máx. 1 MB" : "Max 1 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/users/me/company-logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser(data);
      toast.success(lang === "es" ? "Logo subido" : "Logo uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(lang === "es" ? "¿Eliminar el logo?" : "Delete logo?")) return;
    setDeleting(true);
    try {
      const { data } = await api.delete("/users/me/company-logo");
      setUser(data);
      toast.success(lang === "es" ? "Logo eliminado" : "Logo deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 space-y-4 max-w-2xl" data-testid="company-logo-card">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight">
          {lang === "es" ? "Logo de empresa" : "Company logo"}
        </h2>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {lang === "es"
            ? "Sube el logo de tu empresa. Aparecerá en informes y exportaciones. PNG, JPG o SVG, máx. 1 MB."
            : "Upload your company logo. Used in reports and exports. PNG, JPG or SVG, max 1 MB."}
        </p>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-28 h-28 rounded-md grid place-items-center bg-white border border-[var(--border-color)] overflow-hidden" data-testid="company-logo-preview">
          {logoUrl ? (
            <img src={logoUrl} alt="Company logo" className="w-full h-full object-contain" />
          ) : (
            <ImageIcon size={32} className="text-[var(--text-muted)]" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="company-logo-file-input"
            />
            <span className="inline-flex items-center justify-center text-sm font-medium border border-[var(--border-color)] hover:bg-[var(--surface-hover)] px-3 py-2 rounded-md transition-colors">
              <Upload size={14} className="mr-1.5" />
              {uploading
                ? (lang === "es" ? "Subiendo..." : "Uploading...")
                : logoUrl
                  ? (lang === "es" ? "Cambiar logo" : "Change logo")
                  : (lang === "es" ? "Subir logo" : "Upload logo")}
            </span>
          </label>
          {logoUrl && (
            <Button
              type="button"
              variant="outline"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="company-logo-delete-btn"
              className="text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]"
            >
              <Trash2 size={14} className="mr-1.5" />
              {lang === "es" ? "Eliminar" : "Delete"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================ Team Management
// ============================================================ Change Password
/**
 * Tarjeta de cambio de contraseña propia. Cualquier miembro del tenant
 * (owner/editor/viewer) puede cambiar la suya. El backend exige la contraseña
 * actual como confirmación (POST /auth/change-password).
 */
// ============================================================ Referrals (iter 63)
function ReferralsCard({ lang, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [creating, setCreating] = useState(false);

  const isBasicPlus = user?.plan && ["basic", "pro", "business"].includes(user.plan);
  const rewardsLeft = data?.rewards_remaining ?? 2;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/referrals");
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shareUrl = (code) => `${window.location.origin}/register?ref=${code}`;

  const generate = async () => {
    setCreating(true);
    try {
      const { data: d } = await api.post("/referrals/generate", inviteEmail ? { invitee_email: inviteEmail } : {});
      const url = shareUrl(d.code);
      try {
        await navigator.clipboard.writeText(url);
        toast.success(lang === "es" ? "Enlace copiado al portapapeles" : "Link copied to clipboard", { description: url });
      } catch {
        toast.success(lang === "es" ? "Código generado" : "Code generated", { description: url });
      }
      setInviteEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (code) => {
    const url = shareUrl(code);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(lang === "es" ? "Enlace copiado" : "Link copied");
    } catch {
      toast.info(url);
    }
  };

  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6" data-testid="referrals-card">
      <h2 className="font-display text-xl mb-1 tracking-tight flex items-center gap-2">
        <Gift size={16} className="text-[var(--brand-accent)]" />
        {lang === "es" ? "Referidos" : "Referrals"}
      </h2>
      <p className="text-sm text-[var(--text-secondary)] mb-4">
        {lang === "es"
          ? "Invita a otros restaurantes: cada uno que se suscriba a Basic o superior te regala un mes gratis. Máximo 2 meses por cuenta."
          : "Invite other restaurants: each one who subscribes to Basic or higher gifts you a free month. Max 2 months per account."}
      </p>

      {!isBasicPlus && (
        <div className="rounded-md bg-[var(--surface-hover)] border border-[var(--border-color)] px-3 py-2 text-xs text-[var(--text-secondary)] mb-3" data-testid="referrals-locked">
          <Lock size={12} className="inline mr-1 -mt-0.5" />
          {lang === "es"
            ? "El programa de referidos requiere plan Basic o superior."
            : "Referral program requires Basic plan or higher."}
        </div>
      )}

      {isBasicPlus && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={lang === "es" ? "email del invitado (opcional)" : "invitee email (optional)"}
              className="max-w-sm"
              data-testid="referrals-email-input"
            />
            <Button
              onClick={generate}
              disabled={creating || rewardsLeft <= 0}
              className="bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-white"
              data-testid="referrals-generate-btn"
            >
              <Gift size={12} className="mr-1.5" />
              {creating ? "..." : (lang === "es" ? "Generar enlace" : "Generate link")}
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <StatChip label={lang === "es" ? "Invitados" : "Invited"} value={data?.total ?? 0} testid="ref-stat-total" />
            <StatChip label={lang === "es" ? "Recompensados" : "Rewarded"} value={data?.rewarded ?? 0} testid="ref-stat-rewarded" />
            <StatChip label={lang === "es" ? "Restantes" : "Left"} value={rewardsLeft} testid="ref-stat-remaining" accent />
          </div>
          {loading ? (
            <p className="text-xs text-[var(--text-muted)]">…</p>
          ) : (data?.rows || []).length === 0 ? (
            <p className="text-xs text-[var(--text-muted)]">
              {lang === "es" ? "Aún no has creado ningún enlace de invitación." : "You haven't created any invite link yet."}
            </p>
          ) : (
            <div className="overflow-x-auto" data-testid="referrals-table">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                  <tr className="border-b border-[var(--border-color)]">
                    <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Código" : "Code"}</th>
                    <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Invitado" : "Invitee"}</th>
                    <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Creado" : "Created"}</th>
                    <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Estado" : "Status"}</th>
                    <th className="text-right py-2 px-2 font-medium">{lang === "es" ? "Acciones" : "Actions"}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).map((r) => (
                    <tr key={r.id} className="border-b border-[var(--border-color)]" data-testid={`referral-row-${r.code}`}>
                      <td className="py-2 px-2 font-mono-num">{r.code}</td>
                      <td className="py-2 px-2">{r.invitee_email || <span className="text-[var(--text-muted)]">—</span>}</td>
                      <td className="py-2 px-2 font-mono-num">{formatDateShort(r.created_at)}</td>
                      <td className="py-2 px-2">
                        {r.rewarded_at ? (
                          <span className="inline-flex items-center gap-1 text-[var(--status-positive)] text-xs">
                            <Check size={12} /> {lang === "es" ? "Recompensado" : "Rewarded"}
                          </span>
                        ) : r.redeemed_at ? (
                          <span className="text-xs text-[var(--brand-accent)]">
                            {lang === "es" ? "Registrado" : "Registered"}
                          </span>
                        ) : (
                          <span className="text-xs text-[var(--text-muted)]">
                            {lang === "es" ? "Pendiente" : "Pending"}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyLink(r.code)}
                          data-testid={`ref-copy-${r.code}`}
                          className="h-7"
                        >
                          <Copy size={12} className="mr-1" />
                          {lang === "es" ? "Copiar" : "Copy"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatChip({ label, value, accent, testid }) {
  return (
    <div
      className={`border rounded-md px-3 py-2 text-center ${accent ? "border-[var(--brand-accent)]/30 bg-[var(--brand-accent)]/5" : "border-[var(--border-color)]"}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{label}</div>
      <div className={`font-display text-xl font-bold ${accent ? "text-[var(--brand-accent)]" : ""}`}>{value}</div>
    </div>
  );
}

function ChangePasswordCard({ lang }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 6) {
      toast.error(lang === "es" ? "La nueva contraseña debe tener al menos 6 caracteres" : "New password must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      toast.error(lang === "es" ? "Las contraseñas no coinciden" : "Passwords do not match");
      return;
    }
    setSaving(true);
    try {
      const resp = await api.post("/auth/change-password", { current_password: current, new_password: next });
      // SEC-003: backend rotates pwd_epoch and returns a fresh token so this
      // session keeps working after the change (otherwise the pre-change token
      // would 401 on the very next request).
      if (resp?.data?.access_token) {
        setToken(resp.data.access_token);
      }
      toast.success(lang === "es" ? "Contraseña actualizada" : "Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6" data-testid="change-password-card">
      <h2 className="font-display text-xl font-semibold tracking-tight mb-1">
        {lang === "es" ? "Cambiar contraseña" : "Change password"}
      </h2>
      <p className="text-xs text-[var(--text-muted)] mb-4">
        {lang === "es"
          ? "Necesitas confirmar tu contraseña actual. Mínimo 6 caracteres para la nueva."
          : "You must confirm your current password. New password min 6 characters."}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl">
        <Field label={lang === "es" ? "Contraseña actual" : "Current password"}>
          <Input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            data-testid="change-password-current"
          />
        </Field>
        <Field label={lang === "es" ? "Nueva contraseña" : "New password"}>
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            data-testid="change-password-new"
            placeholder={lang === "es" ? "Mín. 6 caracteres" : "Min 6 characters"}
          />
        </Field>
        <Field label={lang === "es" ? "Repetir nueva" : "Confirm new"}>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            data-testid="change-password-confirm"
          />
        </Field>
      </div>
      <div className="mt-4">
        <Button
          onClick={submit}
          disabled={saving || !current || !next || !confirm}
          data-testid="change-password-submit"
          className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
        >
          {saving ? "..." : (lang === "es" ? "Actualizar contraseña" : "Update password")}
        </Button>
      </div>
    </div>
  );
}

function TeamManagementCard({ lang, ownerEmail }) {
  const [members, setMembers] = useState([]);
  const [invited, setInvited] = useState([]);
  const [permsOpen, setPermsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Iter 64b — invite an existing user (owner of another restaurant) to
  // this workspace. Backend endpoint: POST /team/invite-existing.
  const [inviteExistingOpen, setInviteExistingOpen] = useState(false);
  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState("editor");
  const [inviting, setInviting] = useState(false);
  const submitInviteExisting = async () => {
    const email = (invEmail || "").trim().toLowerCase();
    if (!email) return;
    setInviting(true);
    try {
      const { data } = await api.post("/team/invite-existing", { email, role: invRole });
      toast.success(
        lang === "es"
          ? `Invitación enviada: ${data.invitee_email} ahora tiene acceso como ${invRole}`
          : `Invited: ${data.invitee_email} now has ${invRole} access`,
      );
      setInvEmail("");
      setInviteExistingOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInviting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, iRes] = await Promise.all([
        api.get("/team"),
        api.get("/team/invited").catch(() => ({ data: { invited: [] } })),
      ]);
      setMembers(tRes.data.members || []);
      setInvited(iRes.data.invited || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (m) => {
    if (!window.confirm(lang === "es"
      ? `¿Eliminar al usuario ${m.email}? Esta acción es irreversible.`
      : `Delete user ${m.email}? This is irreversible.`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success(lang === "es" ? "Usuario eliminado" : "User deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Iter 82.7m — invited-guest actions (external identities added via
  // /team/invite-existing). Editing changes their role on THIS tenant
  // only; deleting flips `active:false` (they keep their own account).
  const handleInvitedRoleChange = async (row, newRole) => {
    if (newRole === row.role) return;
    try {
      await api.patch(`/team/membership/${row.identity_id}`, { role: newRole });
      toast.success(lang === "es" ? "Rol actualizado" : "Role updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleInvitedDelete = async (row) => {
    if (!window.confirm(lang === "es"
      ? `¿Expulsar a ${row.email} de este workspace? Su cuenta seguirá existiendo pero perderá acceso.`
      : `Remove ${row.email} from this workspace? Their account stays, they just lose access.`)) return;
    try {
      await api.delete(`/team/membership/${row.identity_id}`);
      toast.success(lang === "es" ? "Invitado expulsado" : "Guest removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const roleLabel = (role) => {
    if (role === "owner") return lang === "es" ? "Propietario" : "Owner";
    if (role === "editor") return lang === "es" ? "Editor" : "Editor";
    if (role === "viewer") return lang === "es" ? "Lector" : "Viewer";
    return role;
  };

  const roleClass = (role) => ({
    owner: "bg-[var(--brand-accent)]/15 text-[var(--brand-accent)]",
    editor: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    viewer: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  }[role] || "bg-slate-100 text-slate-700");

  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 space-y-4" data-testid="team-management-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">
            {lang === "es" ? "Equipo y permisos" : "Team & permissions"}
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
            {lang === "es"
              ? "Crea cuentas para tu equipo. Editores pueden gestionar facturas y datos. Lectores solo pueden consultar."
              : "Create accounts for your team. Editors can manage invoices and data. Viewers are read-only."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => setPermsOpen(true)}
            variant="outline"
            data-testid="team-view-permissions-btn"
          >
            <ShieldCheck size={14} className="mr-1.5" />
            {lang === "es" ? "Ver permisos" : "View permissions"}
          </Button>
          <Button
            onClick={() => setInviteExistingOpen(true)}
            variant="outline"
            data-testid="team-invite-existing-btn"
          >
            <UserPlus size={14} className="mr-1.5" />
            {lang === "es" ? "Invitar existente" : "Invite existing"}
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="team-create-btn"
            className="bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-white"
          >
            <UserPlus size={14} className="mr-1.5" />
            {lang === "es" ? "Añadir usuario" : "Add user"}
          </Button>
        </div>
      </div>

      <div className="border border-[var(--border-color)] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="team-members-table">
          <thead className="bg-[var(--surface-hover)] text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
            <tr>
              <th className="text-left py-2 px-3 font-medium">{lang === "es" ? "Email" : "Email"}</th>
              <th className="text-left py-2 px-3 font-medium">{lang === "es" ? "Nombre" : "Name"}</th>
              <th className="text-left py-2 px-3 font-medium">{lang === "es" ? "Rol" : "Role"}</th>
              <th className="text-right py-2 px-3 font-medium"> </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="4" className="text-center py-6 text-[var(--text-muted)]">…</td></tr>
            )}
            {!loading && members.map((m) => (
              <tr key={m.id} className="border-t border-[var(--border-color)]" data-testid={`team-member-${m.id}`}>
                <td className="py-2 px-3 font-mono-num">{m.email}</td>
                <td className="py-2 px-3">{m.name || "—"}</td>
                <td className="py-2 px-3">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${roleClass(m.role)}`}>
                    {roleLabel(m.role)}
                  </span>
                </td>
                <td className="py-2 px-3 text-right">
                  {m.role !== "owner" && (
                    <div className="inline-flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(m)}
                        data-testid={`team-edit-${m.id}`}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(m)}
                        data-testid={`team-delete-${m.id}`}
                        className="text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TeamCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
        lang={lang}
      />
      <TeamEditDialog
        member={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
        lang={lang}
      />

      {/* Iter 82.7m — Invited guests table (external identities via /team/invite-existing) */}
      {invited.length > 0 && (
        <div className="border border-[var(--border-color)] rounded-md overflow-hidden" data-testid="team-invited-card">
          <div className="bg-[var(--surface-hover)] px-3 py-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)] flex items-center justify-between">
            <span>{lang === "es" ? "Invitados externos" : "Invited guests"}</span>
            <span className="text-[10px]">{invited.length}</span>
          </div>
          <table className="w-full text-sm" data-testid="team-invited-table">
            <thead className="bg-[var(--surface-hover)]/50 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              <tr>
                <th className="text-left py-1.5 px-3">{lang === "es" ? "Email" : "Email"}</th>
                <th className="text-left py-1.5 px-3">{lang === "es" ? "Rol en tu workspace" : "Role in your workspace"}</th>
                <th className="text-left py-1.5 px-3">{lang === "es" ? "Invitado el" : "Invited"}</th>
                <th className="text-right py-1.5 px-3 w-24"> </th>
              </tr>
            </thead>
            <tbody>
              {invited.map((row) => (
                <tr key={row.identity_id} className="border-t border-[var(--border-color)]" data-testid={`team-invited-${row.identity_id}`}>
                  <td className="py-2 px-3">
                    <p className="font-medium truncate">{row.email}</p>
                    {row.name && <p className="text-[10px] text-[var(--text-muted)]">{row.name}</p>}
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={row.role}
                      onChange={(e) => handleInvitedRoleChange(row, e.target.value)}
                      className="h-8 rounded-md border border-[var(--border-color)] bg-[var(--surface)] px-2 text-xs"
                      data-testid={`team-invited-role-${row.identity_id}`}
                    >
                      <option value="editor">{lang === "es" ? "Editor" : "Editor"}</option>
                      <option value="viewer">{lang === "es" ? "Lector" : "Viewer"}</option>
                    </select>
                  </td>
                  <td className="py-2 px-3 text-xs text-[var(--text-muted)] font-mono-num">
                    {row.invited_at ? row.invited_at.slice(0, 10) : "—"}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleInvitedDelete(row)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400"
                      data-testid={`team-invited-delete-${row.identity_id}`}
                      title={lang === "es" ? "Expulsar de este workspace" : "Remove from this workspace"}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {permsOpen && <RolePermissionsDialog lang={lang} onClose={() => setPermsOpen(false)} />}

      {/* Iter 64b — Invite existing user dialog */}
      <Dialog open={inviteExistingOpen} onOpenChange={(o) => !inviting && setInviteExistingOpen(o)}>
        <DialogContent data-testid="team-invite-existing-dialog">
          <DialogHeader>
            <DialogTitle>{lang === "es" ? "Invitar usuario existente" : "Invite existing user"}</DialogTitle>
            <DialogDescription>
              {lang === "es"
                ? "Introduce el email de un usuario que YA tenga una cuenta en ChefSync (p.ej. el propietario de otro restaurante). Podrá cambiar entre su cuenta y la tuya desde el selector."
                : "Enter the email of a user who ALREADY has a ChefSync account (e.g. an owner of another restaurant). They will be able to switch between their workspace and yours from the selector."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Field label={lang === "es" ? "Email" : "Email"}>
              <Input
                type="email"
                value={invEmail}
                onChange={(e) => setInvEmail(e.target.value)}
                data-testid="team-invite-existing-email"
                placeholder="user@example.com"
                autoFocus
              />
            </Field>
            <Field label={lang === "es" ? "Rol" : "Role"}>
              <select
                value={invRole}
                onChange={(e) => setInvRole(e.target.value)}
                className="h-10 w-full px-3 rounded-md border border-[var(--border-color)] bg-[var(--surface)] text-sm"
                data-testid="team-invite-existing-role"
              >
                <option value="editor">{lang === "es" ? "Editor (leer + escribir)" : "Editor (read + write)"}</option>
                <option value="viewer">{lang === "es" ? "Lector (solo consulta)" : "Viewer (read-only)"}</option>
              </select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteExistingOpen(false)} disabled={inviting}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={submitInviteExisting}
              disabled={inviting || !invEmail.trim()}
              data-testid="team-invite-existing-confirm"
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            >
              {inviting ? "..." : (lang === "es" ? "Invitar" : "Invite")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeamCreateDialog({ open, onClose, onCreated, lang }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("editor");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setEmail(""); setPassword(""); setName(""); setRole("editor"); }
  }, [open]);

  const submit = async () => {
    if (!email || password.length < 6) {
      toast.error(lang === "es" ? "Email y contraseña (mín. 6) requeridos" : "Email and password (min 6) required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/team", { email, password, name: name || null, role });
      toast.success(lang === "es" ? "Usuario creado" : "User created");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="team-create-dialog">
        <DialogHeader>
          <DialogTitle>{lang === "es" ? "Añadir usuario al equipo" : "Add team user"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{lang === "es" ? "Email" : "Email"}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="team-create-email" placeholder="user@example.com" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{lang === "es" ? "Nombre" : "Name"}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="team-create-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{lang === "es" ? "Contraseña" : "Password"}</Label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="team-create-password" placeholder={lang === "es" ? "Mínimo 6 caracteres" : "Min 6 characters"} />
            <p className="text-xs text-[var(--text-muted)]">
              {lang === "es" ? "Comparte estas credenciales con el usuario. Podrá iniciar sesión inmediatamente." : "Share these credentials with the user. They can log in right away."}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{lang === "es" ? "Rol" : "Role"}</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="team-create-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="editor">{lang === "es" ? "Editor — puede crear y editar" : "Editor — can create and edit"}</SelectItem>
                <SelectItem value="viewer">{lang === "es" ? "Lector — solo consulta" : "Viewer — read-only"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lang === "es" ? "Cancelar" : "Cancel"}</Button>
          <Button onClick={submit} disabled={saving} data-testid="team-create-submit" className="bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-white">
            {saving ? "..." : (lang === "es" ? "Crear" : "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamEditDialog({ member, onClose, onSaved, lang }) {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("editor");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member) {
      setName(member.name || "");
      setPassword("");
      setRole(member.role || "editor");
    }
  }, [member]);

  if (!member) return null;

  const submit = async () => {
    const payload = {};
    if (name !== (member.name || "")) payload.name = name;
    if (role !== member.role) payload.role = role;
    if (password) {
      if (password.length < 6) {
        toast.error(lang === "es" ? "Contraseña mínima 6 caracteres" : "Password min 6 chars");
        return;
      }
      payload.password = password;
    }
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/team/${member.id}`, payload);
      toast.success(lang === "es" ? "Usuario actualizado" : "User updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="team-edit-dialog">
        <DialogHeader>
          <DialogTitle>{lang === "es" ? "Editar usuario" : "Edit user"} — {member.email}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{lang === "es" ? "Nombre" : "Name"}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="team-edit-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{lang === "es" ? "Nueva contraseña" : "New password"}</Label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="team-edit-password" placeholder={lang === "es" ? "Dejar vacío para no cambiar" : "Leave blank to keep current"} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{lang === "es" ? "Rol" : "Role"}</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="team-edit-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="editor">{lang === "es" ? "Editor" : "Editor"}</SelectItem>
                <SelectItem value="viewer">{lang === "es" ? "Lector" : "Viewer"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lang === "es" ? "Cancelar" : "Cancel"}</Button>
          <Button onClick={submit} disabled={saving} data-testid="team-edit-submit" className="bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-white">
            {saving ? "..." : (lang === "es" ? "Guardar" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{label}</Label>
      {children}
    </div>
  );
}

function planLabel(plan, t) {
  return ({ free: t("planFree"), basic: t("planBasic"), pro: t("planPro"), business: t("planBusiness") })[plan] || plan;
}

function SubscriptionStatus({ usage }) {
  const { t, lang } = useI18n();
  if (!usage) {
    return (
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 h-32 animate-pulse" />
    );
  }
  const used = usage.usage.invoices_used;
  const limit = usage.usage.invoices_limit;
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const isNearLimit = limit != null && used >= limit * 0.75;
  const isAtLimit = limit != null && used >= limit;
  const planName = planLabel(usage.plan, t);
  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 space-y-4" data-testid="subscription-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-[var(--text-muted)]">{t("currentPlan")}</p>
          <div className="flex items-center gap-2 mt-1">
            <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="current-plan-name">
              {planName}
            </h2>
            <span
              className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-[var(--brand-accent)]/15 text-[var(--brand-accent)]"
            >
              {planName}
            </span>
          </div>
          {usage.billing_cycle && usage.plan !== "free" && (
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {usage.billing_cycle === "yearly" ? t("yearly") : t("monthly")}
            </p>
          )}
        </div>
      </div>

      {usage.is_trialing && (
        <div
          data-testid="trial-banner"
          className="rounded-md border border-[var(--brand-accent)]/30 bg-[var(--brand-accent)]/5 px-3 py-2.5 text-xs flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="flex items-center gap-2">
            <span className="text-base">🎁</span>
            <div>
              <p className="font-semibold text-[var(--brand-accent)] uppercase tracking-wider text-[10px]">
                {lang === "es" ? "Prueba Business activa" : "Business trial active"}
              </p>
              <p className="text-[var(--text-secondary)]">
                {lang === "es"
                  ? `Te quedan ${usage.trial_days_left} días`
                  : `${usage.trial_days_left} days left`}
                {usage.trial_ends_at && (
                  <span className="text-[var(--text-muted)]"> · {formatDateShort(usage.trial_ends_at)}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <div data-testid="usage-progress">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-[var(--text-secondary)]">{t("monthlyUsage")} · {usage.usage.month}</span>
          <span className={`font-mono-num font-medium ${isAtLimit ? "text-[var(--status-negative)]" : isNearLimit ? "text-[var(--status-warning)]" : ""}`}>
            {used}{limit != null ? ` / ${limit}` : ""} {t("invoicesUsed").toLowerCase()}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-[var(--surface-hover)] overflow-hidden">
          <div
            className={`h-full transition-all ${
              isAtLimit
                ? "bg-[var(--status-negative)]"
                : isNearLimit
                  ? "bg-[var(--status-warning)]"
                  : "bg-[var(--brand-accent)]"
            }`}
            style={{ width: limit ? `${pct}%` : "100%" }}
          />
        </div>
        {limit == null && (
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-1">{t("planUnlimited")}</p>
        )}
        {isAtLimit && (
          <p className="text-xs text-[var(--status-negative)] mt-2">{t("limitReached")}</p>
        )}
      </div>
    </div>
  );
}

function PricingTable({ plans, currentPlan, billingCycle, onCycleChange, onSelect }) {
  const { t } = useI18n();
  if (!plans || plans.length === 0) return null;
  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 space-y-6" data-testid="pricing-table">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold tracking-tight">{t("chooseYourPlan")}</h3>
          <p className="text-sm text-[var(--text-secondary)] mt-1">{t("pricingSubtitle")}</p>
        </div>
        <BillingCycleToggle value={billingCycle} onChange={onCycleChange} />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {plans.map((p) => (
          <PlanCard
            key={p.id}
            plan={p}
            billingCycle={billingCycle}
            isCurrent={p.id === currentPlan}
            onSelect={() => onSelect(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function BillingCycleToggle({ value, onChange }) {
  const { t } = useI18n();
  return (
    <div className="inline-flex items-center bg-[var(--surface-hover)] rounded-md p-0.5 text-xs" data-testid="billing-cycle-toggle">
      <button
        type="button"
        data-testid="cycle-monthly"
        onClick={() => onChange("monthly")}
        className={`px-3 py-1.5 rounded transition-colors ${value === "monthly" ? "bg-[var(--surface)] shadow-sm font-semibold" : "text-[var(--text-secondary)]"}`}
      >
        {t("monthly")}
      </button>
      <button
        type="button"
        data-testid="cycle-yearly"
        onClick={() => onChange("yearly")}
        className={`px-3 py-1.5 rounded transition-colors flex items-center gap-1.5 ${value === "yearly" ? "bg-[var(--surface)] shadow-sm font-semibold" : "text-[var(--text-secondary)]"}`}
      >
        {t("yearly")}
        <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[var(--brand-accent)]/15 text-[var(--brand-accent)]">
          {t("yearlyDiscount")}
        </span>
      </button>
    </div>
  );
}

function PlanFeatureRows({ plan }) {
  const { lang } = useI18n();
  // Order matters — reflects the sidebar order the user is used to.
  const catalog = [
    { key: "ai_ocr",        es: "OCR con IA",              en: "AI OCR" },
    { key: "bulk_upload",   es: "Subida masiva",           en: "Bulk upload" },
    { key: "menu",          es: "Escandallos y carta",     en: "Menu costing" },
    { key: "inventory",     es: "Inventario",              en: "Inventory" },
    { key: "albaranes",     es: "Albaranes",               en: "Delivery notes" },
    { key: "budget",        es: "Presupuesto",             en: "Budget" },
    { key: "client_debts",  es: "Deudas de clientes",      en: "Client debts" },
    { key: "treasury",      es: "Tesorería",               en: "Treasury" },
  ];
  const features = plan.features || {};
  // If the backend hasn't sent per-plan features yet (older cached response),
  // fall back to the generic "all included" bullet — matches previous UX.
  if (!plan.features) {
    return (
      <li className="flex items-center gap-2">
        <Check size={12} className="text-[var(--brand-accent)] shrink-0" />
        <span>{lang === "es" ? "Todas las funciones incluidas" : "All features included"}</span>
      </li>
    );
  }
  return catalog.map(({ key, es, en }) => {
    const on = !!features[key];
    return (
      <li key={key} className="flex items-center gap-2" data-testid={`plan-feature-${plan.id}-${key}`}>
        {on ? (
          <Check size={12} className="text-[var(--brand-accent)] shrink-0" />
        ) : (
          <XCircle size={12} className="text-[var(--text-muted)] shrink-0" />
        )}
        <span className={on ? "" : "text-[var(--text-muted)] line-through"}>
          {lang === "es" ? es : en}
        </span>
      </li>
    );
  });
}


function PlanCard({ plan, billingCycle, isCurrent, onSelect }) {
  const { t } = useI18n();
  const price = billingCycle === "yearly" ? plan.price_yearly : plan.price_monthly;
  const suffix = billingCycle === "yearly" ? t("perYear") : t("perMonth");
  const highlighted = plan.highlight;
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative rounded-lg p-5 border flex flex-col gap-4 transition-all ${
        highlighted
          ? "border-[var(--brand-accent)] bg-[var(--brand-accent)]/5 shadow-sm"
          : "border-[var(--border-color)] bg-[var(--surface)]"
      } ${isCurrent ? "ring-2 ring-[var(--brand-accent)]" : ""}`}
    >
      {highlighted && (
        <span className="absolute -top-2.5 left-4 text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[var(--brand-accent)] text-white flex items-center gap-1">
          <Crown size={10} /> Popular
        </span>
      )}

      <div>
        <h4 className="font-display text-xl font-bold tracking-tight">{plan.name}</h4>
        <div className="mt-2 flex items-baseline gap-1">
          <span className="font-display text-3xl font-bold tracking-tight font-mono-num">
            {price}€
          </span>
          {price > 0 && <span className="text-xs text-[var(--text-muted)]">{suffix}</span>}
        </div>
        {billingCycle === "yearly" && plan.price_monthly > 0 && (
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-1">
            ≈ {Math.round(plan.price_yearly / 12)}€{t("perMonth")}
          </p>
        )}
      </div>

      <ul className="text-xs space-y-1.5 text-[var(--text-secondary)] flex-1" data-testid={`plan-features-${plan.id}`}>
        <li className="flex items-center gap-2">
          <Check size={12} className="text-[var(--brand-accent)] shrink-0" />
          <span><strong className="text-[var(--text-primary)]">{plan.monthly_invoices}</strong> {t("invoicesPerMonth")}</span>
        </li>
        <PlanFeatureRows plan={plan} />
      </ul>

      <Button
        onClick={onSelect}
        disabled={isCurrent}
        data-testid={`plan-select-${plan.id}`}
        variant={highlighted ? "default" : "outline"}
        className={highlighted ? "bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-white w-full" : "w-full"}
      >
        {isCurrent ? (
          <>
            <Check size={14} className="mr-1.5" /> {t("yourCurrentPlan")}
          </>
        ) : (
          <>
            <Sparkles size={14} className="mr-1.5" /> {t("selectThisPlan")}
          </>
        )}
      </Button>
    </div>
  );
}



// ================================================================
// Iter 82.7m — Role permissions matrix. Static text describing what
// each role (Owner, Editor, Viewer) can and cannot do in the app.
// Kept in one component so a single source of truth exists next to
// the Team card. Consult server.py `_VIEWER_HIDDEN_PREFIXES` and
// `_VIEWER_BYPASS_PREFIXES` when the underlying rules change.
// ================================================================
function RolePermissionsDialog({ lang, onClose }) {
  // iter 82.7x — matriz ahora editable por el owner. Las defaults vienen del
  // backend; cualquier override se persiste en db.role_permissions.
  const [matrix, setMatrix] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/role-permissions");
        setMatrix(data.matrix || {});
        setDefaults(data.defaults || {});
        setCanEdit(!!data.can_edit);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const rows = [
    { key: "invoices",    es: "Gastos (facturas)",   en: "Expenses (invoices)" },
    { key: "income",      es: "Ingresos",            en: "Income" },
    { key: "salaries",    es: "Salarios",            en: "Payroll" },
    { key: "products",    es: "Productos y precios", en: "Products & prices" },
    { key: "vendors",     es: "Proveedores",         en: "Vendors" },
    { key: "carta",       es: "Carta / Escandallos", en: "Menu / Recipes" },
    { key: "inventory",   es: "Inventario",          en: "Inventory" },
    { key: "albaranes",   es: "Albaranes",           en: "Delivery notes" },
    { key: "budget",      es: "Presupuesto",         en: "Budget" },
    { key: "client_debts",es: "Deudas de clientes",  en: "Client debts" },
    { key: "treasury",    es: "Tesorería",           en: "Treasury" },
    { key: "categories",  es: "Categorías",          en: "Categories" },
    { key: "settings",    es: "Ajustes del negocio", en: "Business settings" },
    { key: "team",        es: "Equipo y permisos",   en: "Team & permissions" },
    { key: "billing",     es: "Plan y suscripción",  en: "Plan & subscription" },
    { key: "alerts",      es: "Marcar alertas visto",en: "Dismiss alerts" },
    { key: "profile",     es: "Perfil propio",       en: "Own profile" },
  ];

  const label = (v) => {
    if (v === "rw") return lang === "es" ? "Editar" : "Edit";
    if (v === "r")  return lang === "es" ? "Solo ver" : "View only";
    return lang === "es" ? "Sin acceso" : "No access";
  };
  const badgeCls = (v) => {
    if (v === "rw") return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300";
    if (v === "r")  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
    return                "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300";
  };

  const setCell = (modKey, role, value) => {
    setMatrix((prev) => ({
      ...(prev || {}),
      [modKey]: { ...(prev?.[modKey] || {}), [role]: value },
    }));
    setDirty(true);
  };

  const saveMatrix = async () => {
    setSaving(true);
    try {
      await api.put("/team/role-permissions", { matrix });
      toast.success(lang === "es" ? "Permisos guardados" : "Permissions saved");
      setDirty(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm(lang === "es"
      ? "¿Restaurar los permisos por defecto? Perderás tu configuración actual."
      : "Reset to default permissions? Your current changes will be lost.")) return;
    setSaving(true);
    try {
      const { data } = await api.delete("/team/role-permissions");
      setMatrix(data.matrix || defaults || {});
      setDirty(false);
      toast.success(lang === "es" ? "Permisos restaurados" : "Permissions reset");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && (!dirty || window.confirm(lang === "es" ? "Tienes cambios sin guardar. ¿Cerrar?" : "You have unsaved changes. Close?")) && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="role-permissions-dialog">
        <DialogHeader>
          <DialogTitle>{lang === "es" ? "Permisos por rol" : "Role permissions"}</DialogTitle>
          <DialogDescription>
            {canEdit
              ? (lang === "es"
                  ? "Personaliza qué puede hacer cada rol. El propietario siempre tiene control total."
                  : "Customize what each role can do. Owners always have full control.")
              : (lang === "es"
                  ? "Qué puede ver y hacer cada rol en tu workspace. El propietario es el único que puede editar estos permisos."
                  : "What each role can do in your workspace. Only owners can edit these permissions.")}
          </DialogDescription>
        </DialogHeader>
        {loading && (
          <div className="py-10 text-center text-[var(--text-muted)] text-sm">…</div>
        )}
        {!loading && matrix && (
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm border border-[var(--border-color)] rounded-md overflow-hidden">
              <thead className="bg-[var(--surface-hover)] text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                <tr>
                  <th className="text-left py-2 px-3">{lang === "es" ? "Módulo" : "Module"}</th>
                  <th className="text-center py-2 px-3">{lang === "es" ? "Propietario" : "Owner"}</th>
                  <th className="text-center py-2 px-3">Editor</th>
                  <th className="text-center py-2 px-3">{lang === "es" ? "Lector" : "Viewer"}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const cur = matrix[r.key] || { editor: "rw", viewer: "r" };
                  return (
                    <tr key={r.key} className="border-t border-[var(--border-color)]">
                      <td className="py-2 px-3 font-medium">{lang === "es" ? r.es : r.en}</td>
                      <td className="py-2 px-3 text-center">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                          {lang === "es" ? "Editar" : "Edit"}
                        </span>
                      </td>
                      {["editor", "viewer"].map((roleKey) => (
                        <td key={roleKey} className="py-2 px-3 text-center">
                          {canEdit ? (
                            <select
                              value={cur[roleKey] || "rw"}
                              onChange={(e) => setCell(r.key, roleKey, e.target.value)}
                              data-testid={`role-perm-${r.key}-${roleKey}`}
                              className={`text-[11px] font-semibold rounded px-2 py-0.5 border border-[var(--border-color)] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--primary)] ${badgeCls(cur[roleKey] || "rw")}`}
                            >
                              <option value="rw">{lang === "es" ? "Editar" : "Edit"}</option>
                              <option value="r">{lang === "es" ? "Solo ver" : "View only"}</option>
                              <option value="-">{lang === "es" ? "Sin acceso" : "No access"}</option>
                            </select>
                          ) : (
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${badgeCls(cur[roleKey] || "rw")}`}>
                              {label(cur[roleKey] || "rw")}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-[11px] text-[var(--text-muted)] mt-3 space-y-0.5">
          <p>
            <strong>{lang === "es" ? "Editar" : "Edit"}:</strong>{" "}
            {lang === "es" ? "puede ver y modificar." : "can view and modify."}
          </p>
          <p>
            <strong>{lang === "es" ? "Solo ver" : "View only"}:</strong>{" "}
            {lang === "es" ? "puede entrar al módulo pero no modificar nada." : "can see the module but cannot modify anything."}
          </p>
          <p>
            <strong>{lang === "es" ? "Sin acceso" : "No access"}:</strong>{" "}
            {lang === "es" ? "no puede ver ni entrar al módulo." : "cannot see or access the module."}
          </p>
        </div>
        <DialogFooter className="gap-2">
          {canEdit && (
            <>
              <Button
                variant="outline"
                onClick={resetDefaults}
                disabled={saving || loading}
                data-testid="role-permissions-reset"
              >
                {lang === "es" ? "Restaurar defaults" : "Reset defaults"}
              </Button>
              <Button
                onClick={saveMatrix}
                disabled={saving || loading || !dirty}
                className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                data-testid="role-permissions-save"
              >
                {saving ? (lang === "es" ? "Guardando…" : "Saving…") : (lang === "es" ? "Guardar" : "Save")}
              </Button>
            </>
          )}
          <Button variant={canEdit ? "ghost" : "default"} onClick={onClose} data-testid="role-permissions-close">
            {lang === "es" ? "Cerrar" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
